import { useEffect, useState } from 'react';
import { TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { accountingApi, ApiError } from '@solara/api-client';
import type { AgingBucketDto } from '@solara/api-client';

export default function AgingPage() {
  const { t } = useTranslation('p1');
  const [data, setData] = useState<{ ar: AgingBucketDto[]; ap: AgingBucketDto[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setData(await accountingApi.aging());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('aging.title')}</h1>
        <p className="text-fg-muted text-sm">{t('aging.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AgingCard
          title={t('aging.accountsReceivable')}
          icon={<TrendingUp className="h-4 w-4 text-success" />}
          buckets={data?.ar ?? []}
          t={t}
        />
        <AgingCard
          title={t('aging.accountsPayable')}
          icon={<TrendingDown className="h-4 w-4 text-danger" />}
          buckets={data?.ap ?? []}
          t={t}
        />
      </div>
    </div>
  );
}

function AgingCard({
  title,
  icon,
  buckets,
  t,
}: {
  title: string;
  icon: React.ReactNode;
  buckets: AgingBucketDto[];
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const total = buckets.reduce((acc, b) => acc + Number(b.amountMinor), 0) / 100;
  const overdue = buckets.filter((b) => b.minDays > 30).reduce((acc, b) => acc + Number(b.amountMinor), 0) / 100;

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <p className="text-xs text-fg-muted">
          {t('aging.totalLabel')}: <strong>{total.toLocaleString()} AED</strong> ·{' '}
          {t('aging.overdueLabel')}: <strong>{overdue.toLocaleString()} AED</strong>
        </p>
      </CardHeader>
      <CardBody>
        {buckets.length === 0 || total === 0 ? (
          <p className="py-6 text-center text-sm text-fg-muted">{t('aging.noInvoices')}</p>
        ) : (
          <ul className="space-y-2">
            {buckets.map((b) => {
              const amount = Number(b.amountMinor) / 100;
              const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
              return (
                <li key={b.label} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {b.label} {t('aging.daysSuffix')}
                    </span>
                    <span className="font-mono text-xs">
                      {amount.toLocaleString()} AED · {b.invoiceCount} {t('aging.invoicesShort')}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                    <div
                      className={`h-full ${b.minDays > 30 ? 'bg-danger' : 'bg-brand-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
